/* eslint-disable */
import { faSend, faExpand } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TextInput, Modal, ModalVariant, TextArea } from '@patternfly/react-core';
import React, { useState, useEffect, useRef } from 'react';
import { ChatActions } from '../../api/lambda/ChatActions';
import { ChatMessage } from '../../api/types';
import { useApplication } from '../user/ApplicationProvider';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';
import Markdown from './Markdown';

export interface Message {
	id: number;
	text: string;
	isUser: boolean;
	sender: 'user' | 'bot';
}

interface ChatInterfaceProps {
	context?: string;
	contextId?: string | number; // When this changes, reset the chat
	/** Render messages externally in a shared thread instead of inside ChatInterface */
	externalThread?: boolean;
	/** Called when messages change, so parent can render them in a shared thread */
	onMessagesChange?: (messages: Message[]) => void;
	/** Called when isThinking changes */
	onThinkingChange?: (isThinking: boolean) => void;
	/** Whether the modal is managed externally */
	showModal?: boolean;
	onModalClose?: () => void;
	/** Extra content to render before messages in the modal (e.g., insights card) */
	modalHeaderContent?: React.ReactNode;
	/** Disable input until context is ready */
	disabled?: boolean;
	/** Called once when the user first types into the input */
	onFirstInteraction?: () => void;
	/** Auto-submit this message on mount (used by Discover "Chat about this") */
	autoSubmitMessage?: string;
}

/** Renders chat messages (for use in external threads) */
export const ChatMessages: React.FC<{
	messages: Message[];
	isThinking: boolean;
}> = ({ messages, isThinking }) => (
	<>
		{messages.map((msg, i) => (
			<div
				key={i}
				className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}
			>
				{msg.sender === 'bot' ? <Markdown>{msg.text}</Markdown> : msg.text}
			</div>
		))}
		{isThinking && (
			<div className="chat-bubble bot">
				<div className="typing-indicator">
					<span className="dot"></span>
					<span className="dot"></span>
					<span className="dot"></span>
				</div>
			</div>
		)}
	</>
);

/** Renders the pinned chat input area */
export const ChatInput: React.FC<{
	input: string;
	setInput: (val: string) => void;
	onSend: () => void;
	isThinking: boolean;
	placeholder?: string;
	disabled?: boolean;
	isModalOpen?: boolean;
}> = ({ input, setInput, onSend, isThinking, placeholder, disabled, isModalOpen }) => {
	const measureRef = useRef<HTMLSpanElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const textInputRef = useRef<any>(null);
	const textAreaRef = useRef<any>(null);
	const [isMultiline, setIsMultiline] = useState(false);

	useEffect(() => {
		if (!measureRef.current || !containerRef.current) return;

		const measure = () => {
			if (!measureRef.current || !containerRef.current) return;

			measureRef.current.textContent = input || ' ';

			const textWidth = measureRef.current.scrollWidth;
			const containerWidth = containerRef.current.clientWidth;

			const ignore = containerWidth == 370 && isModalOpen;

			const offset = isModalOpen ? 183 : 100;

			if (!ignore) {
				setIsMultiline(textWidth + offset > containerWidth);
			}
		};

		// wait for modal/layout transition to finish
		const id = requestAnimationFrame(measure);

		return () => cancelAnimationFrame(id);
	}, [input, isModalOpen]);

	useEffect(() => {
		setIsMultiline(false);
	}, [isModalOpen]);

	useEffect(() => {
		if (isModalOpen) {
			textAreaRef.current?.blur();
			textAreaRef.current?.focus?.();

			setTimeout(() => {
				textInputRef.current?.blur();
				textInputRef.current?.focus?.();
				const len = textInputRef.current.value.length;
				textInputRef.current.setSelectionRange(len, len);
			}, 50);
		}
	}, [isModalOpen]);

	useEffect(() => {
		if (!isMultiline) {
			// wait for DOM swap
			requestAnimationFrame(() => {
				textInputRef.current?.focus?.();
			});
		} else {
			// wait for DOM swap
			requestAnimationFrame(() => {
				textAreaRef.current?.focus?.();
				const len = textAreaRef.current.value.length;
				textAreaRef.current.setSelectionRange(len, len);
			});
		}
	}, [isMultiline]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.shiftKey) {
			e.preventDefault();
			setIsMultiline(true);
			return;
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	return (
		<div
			className="chat-input-group"
			ref={containerRef}
		>
			{/* hidden measurement span */}
			<span
				ref={measureRef}
				style={{
					position: 'absolute',
					visibility: 'hidden',
					whiteSpace: 'pre',
					fontSize: '14px',
					fontFamily: 'inherit',
					letterSpacing: 'inherit',
				}}
			/>

			{isMultiline ? (
				<TextArea
					ref={textAreaRef}
					value={input}
					onChange={setInput}
					onKeyDown={handleKeyPress}
					isDisabled={isThinking || disabled}
					placeholder={placeholder ?? 'Type a message...'}
					autoResize
					rows={2}
					className="taChatMessagePrompt"
				/>
			) : (
				<TextInput
					ref={textInputRef}
					value={input}
					onChange={setInput}
					onKeyDown={handleKeyPress}
					isDisabled={isThinking || disabled}
					placeholder={placeholder ?? 'Type a message...'}
				/>
			)}

			<Button
				onClick={onSend}
				isDisabled={!input.trim() || isThinking || disabled}
				variant="primary"
				className="chat-send-btn"
			>
				<FontAwesomeIcon icon={faSend} />
			</Button>
		</div>
	);
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
	context = '',
	contextId,
	externalThread = false,
	onMessagesChange,
	onThinkingChange,
	showModal,
	onModalClose,
	modalHeaderContent,
	disabled,
	onFirstInteraction,
	autoSubmitMessage,
}) => {
	const { zoneSettings } = useApplication();
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState('');
	const hasInteractedRef = useRef(false);
	const hasAutoSubmittedRef = useRef(false);
	const [isThinking, setIsThinking] = useState(false);
	const [internalShowModal, setInternalShowModal] = useState(false);

	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const chatContainerRef = useRef<HTMLDivElement | null>(null);
	const modalChatContainerRef = useRef<HTMLDivElement | null>(null);

	const isModalOpen = showModal !== undefined ? showModal : internalShowModal;
	const handleModalClose = onModalClose ?? (() => setInternalShowModal(false));

	const scrollToBottom = () => {
		const container = isModalOpen ? modalChatContainerRef.current : chatContainerRef.current;
		if (container) {
			container.scrollTop = container.scrollHeight;
		}
	};

	// Reset chat when graph context changes
	useEffect(() => {
		setMessages([]);
		setInput('');
		hasAutoSubmittedRef.current = false;
	}, [contextId]);

	useEffect(() => {
		onMessagesChange?.(messages);
	}, [messages]);

	useEffect(() => {
		onThinkingChange?.(isThinking);
	}, [isThinking]);

	useEffect(() => {
		if (messages.length > 0 || isThinking) {
			scrollToBottom();
		}
	}, [messages, isThinking, isModalOpen]);

	const sendMessage = async (text: string) => {
		if (!text.trim() || isThinking) return;

		const userMessage: Message = {
			id: Date.now(),
			text,
			isUser: true,
			sender: 'user',
		};

		setMessages((prev) => [...prev, userMessage]);
		setIsThinking(true);

		try {
			// Build full conversation history for API (stateless)
			const apiMessages: ChatMessage[] = [
				...messages.map(
					(m): ChatMessage => ({
						role: m.sender === 'user' ? 'user' : 'assistant',
						content: m.text,
					})
				),
				{ role: 'user', content: text },
			];

			const response = await ChatActions.SendMessage({
				messages: apiMessages,
				context: context,
				metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
			});

			if (response.success && response.data?.response) {
				const aiMessage: Message = {
					id: Date.now() + 1,
					text: response.data.response.content,
					isUser: false,
					sender: 'bot',
				};
				setMessages((prev) => [...prev, aiMessage]);
			} else {
				const errorMessage: Message = {
					id: Date.now() + 1,
					text: 'Sorry, I encountered an error. Please try again.',
					isUser: false,
					sender: 'bot',
				};
				setMessages((prev) => [...prev, errorMessage]);
			}
		} catch (error) {
			const errorMessage: Message = {
				id: Date.now() + 1,
				text: 'Sorry, I encountered an error. Please try again.',
				isUser: false,
				sender: 'bot',
			};
			setMessages((prev) => [...prev, errorMessage]);
		} finally {
			setIsThinking(false);
		}
	};

	const handleSend = async () => {
		if (!input.trim() || isThinking) return;
		const text = input;
		setInput('');
		await sendMessage(text);
	};

	const doSend = () => void handleSend();

	// Auto-submit message from Discover page
	useEffect(() => {
		if (
			autoSubmitMessage &&
			!hasAutoSubmittedRef.current &&
			!disabled &&
			!isThinking &&
			messages.length === 0
		) {
			hasAutoSubmittedRef.current = true;
			void sendMessage(autoSubmitMessage);
		}
	}, [autoSubmitMessage, disabled, isThinking, messages.length]);

	const handleInputChange = (value: string) => {
		setInput(value);
		if (!hasInteractedRef.current && value.length > 0) {
			hasInteractedRef.current = true;
			onFirstInteraction?.();
		}
	};

	// External thread mode: only render the pinned input (messages rendered by parent)
	if (externalThread) {
		return (
			<>
				<ChatInput
					input={input}
					setInput={handleInputChange}
					onSend={doSend}
					isThinking={isThinking}
					disabled={disabled}
					isModalOpen={isModalOpen}
				/>
				<Modal
					variant={ModalVariant.large}
					title="AI * Insights"
					isOpen={isModalOpen}
					onClose={handleModalClose}
					className="chat-modal"
				>
					<div className="chat-container chat-container-modal">
						<div
							className="chat-messages chat-messages-modal"
							ref={modalChatContainerRef}
						>
							{modalHeaderContent}
							<ChatMessages
								messages={messages}
								isThinking={isThinking}
							/>
							<div ref={messagesEndRef} />
						</div>
						<ChatInput
							input={input}
							setInput={setInput}
							onSend={doSend}
							isThinking={isThinking}
							isModalOpen={isModalOpen}
						/>
					</div>
				</Modal>
			</>
		);
	}

	// Legacy inline mode (kept for backward compatibility)
	const chatContent = (isModal: boolean) => (
		<>
			<div
				className={`chat-messages ${isModal ? 'chat-messages-modal' : ''}`}
				ref={isModal ? modalChatContainerRef : chatContainerRef}
			>
				{messages.map((msg, i) => (
					<div
						key={i}
						className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}
					>
						{msg.sender === 'bot' ? <Markdown>{msg.text}</Markdown> : msg.text}
					</div>
				))}

				{isThinking && (
					<div className="chat-bubble bot">
						<div className="typing-indicator">
							<span className="dot"></span>
							<span className="dot"></span>
							<span className="dot"></span>
						</div>
					</div>
				)}

				<div ref={messagesEndRef} />
			</div>

			<div className="chat-input-group">
				<TextInput
					placeholder="Type your message..."
					value={input}
					onChange={(value) => setInput(value)}
					onKeyPress={(e: React.KeyboardEvent) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							doSend();
						}
					}}
					isDisabled={isThinking}
					className="chat-text-input"
				/>
				<Button
					onClick={doSend}
					isDisabled={!input.trim() || isThinking}
					className="chat-send-btn"
					variant="primary"
				>
					<FontAwesomeIcon icon={faSend} />
				</Button>
			</div>
		</>
	);

	return (
		<>
			<div className="chat-container">
				<div className="chat-header">
					<small>
						<FontAwesomeIcon icon={faExpand} /> Follow up question? Prompt to get
						further insight...
					</small>
					<Button
						variant="plain"
						className="chat-expand-btn"
						onClick={() => setInternalShowModal(true)}
						title="Expand chat"
					>
						<FontAwesomeIcon icon={faExpand} />
					</Button>
				</div>
				{chatContent(false)}
			</div>

			<Modal
				variant={ModalVariant.large}
				title="AI Chat"
				isOpen={internalShowModal}
				onClose={() => setInternalShowModal(false)}
				className="chat-modal"
			>
				<div className="chat-container chat-container-modal">{chatContent(true)}</div>
			</Modal>
		</>
	);
};

export default ChatInterface;
